import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { isTouchScreenDevice } from 'utils/isTouchScreenDevice';

import { useSiteConfig } from './useSiteConfig';

type RedirectToAppOptions = {
  appLink: string | null;
  qrCode: string | null;
};

declare global {
  interface Window {
    branch?: BranchSdk;
  }
}

// https://help.branch.io/developers-hub/docs/web-advanced-features
export type BranchSdk = {
  link(
    data: BranchLinkData,
    callback: (err: Error | null, link: string) => void
  ): void;
  qrCode(
    data: BranchLinkData,
    qrCodeSettings: BranchQRCodeSettings,
    callback: (err: Error | null, qrCode: BranchQRCode) => void
  ): void;
};
type BranchLinkData = {
  campaign?: string;
  channel?: string;
  feature?: string;
  stage?: string;
  tags?: string[];
  alias?: string;
  data: Record<string, unknown>;
};
type BranchQRCodeSettings = {
  code_color: `#${string}`;
  background_color: `#${string}`;
  margin: number;
  width: number;
  image_format: 'png' | 'jpg' | 'gif';
};
type BranchQRCode = {
  rawBuffer: Buffer;
  base64(): string;
};

const qrCodeSettings: BranchQRCodeSettings = {
  code_color: '#000000',
  background_color: '#FFFFFF',
  margin: 2,
  width: 380,
  image_format: 'png',
};

export const useBranchFeature = () => {
  const [redirectToApp, setRedirectToApp] = useState<RedirectToAppOptions>({
    appLink: null,
    qrCode: null,
  });
  const { branchAnalyticsData } = useSiteConfig();
  const router = useRouter();
  const isMobile = isTouchScreenDevice();

  useEffect(() => {
    if (!window.branch) return;
    const { asPath, query } = router;

    const linkData: BranchLinkData = {
      data: {
        $canonical_url: `https://${query.country}.puma.com${asPath}`,
        $canonical_identifier: `product-id-${query.id}/${query.model}/swatch-${query.swatch}`,
        '~agency_id': branchAnalyticsData?.agencyId,
      },
    };

    if (isMobile) {
      window.branch.link(linkData, (err, link) => {
        if (!err) {
          setRedirectToApp(prevState => ({ ...prevState, appLink: link }));
        } else {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
    } else {
      window.branch.qrCode(linkData, qrCodeSettings, (err, qrCode) => {
        try {
          const base64 = qrCode.base64();
          setRedirectToApp(prevState => ({
            ...prevState,
            qrCode: base64,
          }));
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(err || error);
        }
      });
    }
    return () =>
      setRedirectToApp({
        appLink: null,
        qrCode: null,
      });
  }, [router, isMobile, branchAnalyticsData?.agencyId]);

  return redirectToApp;
};
